import React, { useState } from 'react';
import { Phone, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import PageLayout from '../components/PageLayout';
import { contactPageData } from '../data/mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------- Validation helpers ----------------
const EMAIL_REGEX = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
// Permissive phone: digits, spaces, +, -, (), . — 7-25 chars
const PHONE_REGEX = /^[\d\s\-+().]{7,25}$/;
// Allow letters (incl. accented), spaces, hyphens, apostrophes, periods
const NAME_REGEX = /^[\p{L}\s\-.'’]+$/u;

const LIMITS = {
  name: { min: 2, max: 80 },
  message: { min: 5, max: 2000 },
  phone: { max: 25 },
};

/**
 * Strip any HTML-like tags and control characters before storing/sending.
 * Defence-in-depth: backend also sanitizes. This gives the user immediate
 * feedback and prevents accidental scripting in the email body.
 */
const sanitizeInput = (value) => {
  if (value == null) return '';
  return String(value)
    .replace(/<[^>]*>/g, '') // strip tags
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ''); // strip control chars
};

const validateField = (name, rawValue) => {
  const value = sanitizeInput(rawValue).trim();
  switch (name) {
    case 'name': {
      if (!value) return 'Name is required.';
      if (value.length < LIMITS.name.min)
        return `Name must be at least ${LIMITS.name.min} characters.`;
      if (value.length > LIMITS.name.max)
        return `Name must be at most ${LIMITS.name.max} characters.`;
      if (!NAME_REGEX.test(value))
        return 'Name contains invalid characters.';
      return '';
    }
    case 'email': {
      if (!value) return 'Email is required.';
      if (value.length > 254) return 'Email is too long.';
      if (!EMAIL_REGEX.test(value)) return 'Please enter a valid email address.';
      return '';
    }
    case 'phone': {
      if (!value) return ''; // optional
      if (value.length > LIMITS.phone.max)
        return `Phone must be at most ${LIMITS.phone.max} characters.`;
      if (!PHONE_REGEX.test(value))
        return 'Please enter a valid phone number.';
      return '';
    }
    case 'message': {
      if (!value) return 'Message is required.';
      if (value.length < LIMITS.message.min)
        return `Message must be at least ${LIMITS.message.min} characters.`;
      if (value.length > LIMITS.message.max)
        return `Message must be at most ${LIMITS.message.max} characters.`;
      return '';
    }
    default:
      return '';
  }
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const max =
      name === 'message'
        ? LIMITS.message.max
        : name === 'name'
        ? LIMITS.name.max
        : name === 'phone'
        ? LIMITS.phone.max
        : 254;
    // Hard cap length at input level to prevent oversized payloads
    const capped = value.slice(0, max);
    setFormData((prev) => ({ ...prev, [name]: capped }));
    setError('');
    if (touched[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, capped) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateAll = () => {
    const errs = {};
    Object.keys(formData).forEach((key) => {
      const msg = validateField(key, formData[key]);
      if (msg) errs[key] = msg;
    });
    setFieldErrors(errs);
    setTouched({ name: true, email: true, phone: true, message: true });
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateAll()) {
      setError('Please fix the errors above before sending.');
      return;
    }
    setLoading(true);
    try {
      // Sanitize before sending
      const payload = {
        name: sanitizeInput(formData.name).trim(),
        email: sanitizeInput(formData.email).trim(),
        phone: sanitizeInput(formData.phone).trim(),
        message: sanitizeInput(formData.message).trim(),
      };
      await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setFieldErrors({});
      setTouched({});
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      // Surface backend validation errors cleanly
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail.length > 0) {
        const messages = detail.map((d) => {
          const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : 'field';
          return `${field}: ${d.msg}`;
        });
        setError(messages.join(' • '));
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } finally {
      setLoading(false);
    }
  };

  const baseInputClass =
    'w-full border rounded-sm px-4 py-3 font-open-sans text-[#333] text-base focus:outline-none transition-colors duration-300';
  const inputClass = (field) =>
    `${baseInputClass} ${
      fieldErrors[field]
        ? 'border-red-400 focus:border-red-500'
        : 'border-gray-200 focus:border-[#C9A84C]'
    }`;

  return (
    <PageLayout scriptLabel={contactPageData.scriptLabel} heading={contactPageData.heading}>
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="font-open-sans text-[#666] text-lg leading-relaxed text-center mb-12 max-w-2xl mx-auto">
            {contactPageData.intro}
          </p>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              {submitted && (
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-sm p-4 mb-6">
                  <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                  <p className="font-open-sans text-green-700 text-sm">
                    Thank you for reaching out! We'll get back to you soon.
                  </p>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-sm p-4 mb-6">
                  <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="font-open-sans text-red-700 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div>
                  <label htmlFor="contact-name" className="font-open-sans text-[#4A154B] text-sm tracking-[0.1em] uppercase block mb-2">
                    Name *
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    autoComplete="name"
                    maxLength={LIMITS.name.max}
                    aria-invalid={!!fieldErrors.name}
                    aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
                    className={inputClass('name')}
                  />
                  {fieldErrors.name && (
                    <p id="contact-name-error" className="mt-1 text-xs text-red-600 font-open-sans">
                      {fieldErrors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="contact-email" className="font-open-sans text-[#4A154B] text-sm tracking-[0.1em] uppercase block mb-2">
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    autoComplete="email"
                    maxLength={254}
                    aria-invalid={!!fieldErrors.email}
                    aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
                    className={inputClass('email')}
                  />
                  {fieldErrors.email && (
                    <p id="contact-email-error" className="mt-1 text-xs text-red-600 font-open-sans">
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="contact-phone" className="font-open-sans text-[#4A154B] text-sm tracking-[0.1em] uppercase block mb-2">
                    Phone
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="tel"
                    maxLength={LIMITS.phone.max}
                    inputMode="tel"
                    aria-invalid={!!fieldErrors.phone}
                    aria-describedby={fieldErrors.phone ? 'contact-phone-error' : undefined}
                    className={inputClass('phone')}
                  />
                  {fieldErrors.phone && (
                    <p id="contact-phone-error" className="mt-1 text-xs text-red-600 font-open-sans">
                      {fieldErrors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="contact-message" className="font-open-sans text-[#4A154B] text-sm tracking-[0.1em] uppercase">
                      Message *
                    </label>
                    <span className="font-open-sans text-xs text-gray-400">
                      {formData.message.length}/{LIMITS.message.max}
                    </span>
                  </div>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    rows={5}
                    maxLength={LIMITS.message.max}
                    aria-invalid={!!fieldErrors.message}
                    aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
                    className={`${inputClass('message')} resize-none`}
                  />
                  {fieldErrors.message && (
                    <p id="contact-message-error" className="mt-1 text-xs text-red-600 font-open-sans">
                      {fieldErrors.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-8 py-3 bg-[#C9A84C] text-white font-open-sans text-xs tracking-[0.2em] uppercase hover:bg-[#B89740] transition-colors duration-300 ${
                    loading ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  <Send size={16} />
                  {loading ? 'SENDING...' : 'SEND MESSAGE'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-[#F3ECF8] rounded-sm p-8">
                <div className="flex items-start gap-4 mb-8">
                  <Phone size={24} className="text-[#C9A84C] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-open-sans text-[#4A154B] text-sm tracking-[0.15em] font-bold uppercase mb-2">
                      Phone
                    </h3>
                    <a
                      href="tel:7196842180"
                      className="font-playfair text-[#4A154B] text-xl font-bold hover:text-[#C9A84C] transition-colors duration-300"
                    >
                      {contactPageData.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mail size={24} className="text-[#C9A84C] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-open-sans text-[#4A154B] text-sm tracking-[0.15em] font-bold uppercase mb-2">
                      Email
                    </h3>
                    <a
                      href="mailto:painfree@ahealingtouchenergy.com"
                      className="font-open-sans text-[#666] text-base hover:text-[#C9A84C] transition-colors duration-300 break-all"
                    >
                      painfree@ahealingtouchenergy.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ContactPage;
